/**
 * O que o Levitas guarda para ler sem rede (seção 9.5).
 *
 * Guardado **no aplicativo**, e não no service worker, por um motivo de
 * segurança e não de conveniência: o cache do service worker é indexado por URL
 * e compartilhado por todas as sessões do navegador. Numa igreja, o tablet da
 * secretaria passa de mão em mão — guardar a resposta de `/me/assignments` ali
 * serviria a escala de uma pessoa para a próxima que entrasse.
 *
 * Aqui a chave carrega **quem** e **em qual igreja**. Sessão diferente lê chave
 * diferente, e ao sair tudo é apagado.
 */

const PREFIXO = 'levitas:offline:';

interface Guardado<T> {
  em: string;
  dados: T;
}

function chave(assunto: string, userId: string, tenantId: string | null): string {
  return `${PREFIXO}${assunto}:${userId}:${tenantId ?? 'sem-igreja'}`;
}

export function guardarOffline<T>(
  assunto: string,
  quem: { userId: string; tenantId: string | null },
  dados: T,
): void {
  try {
    const conteudo: Guardado<T> = { em: new Date().toISOString(), dados };
    localStorage.setItem(chave(assunto, quem.userId, quem.tenantId), JSON.stringify(conteudo));
  } catch {
    // Armazenamento cheio ou bloqueado: o produto continua funcionando online,
    // e só perde a leitura sem rede. Nada quebra por causa disso.
  }
}

export function lerOffline<T>(
  assunto: string,
  quem: { userId: string; tenantId: string | null },
): { em: Date; dados: T } | null {
  try {
    const cru = localStorage.getItem(chave(assunto, quem.userId, quem.tenantId));
    if (!cru) return null;

    const guardado = JSON.parse(cru) as Guardado<T>;
    return { em: new Date(guardado.em), dados: guardado.dados };
  } catch {
    return null;
  }
}

/** Ao sair, nada do que era daquela pessoa fica no aparelho. */
export function limparOffline(): void {
  try {
    const chaves = Object.keys(localStorage).filter((item) => item.startsWith(PREFIXO));
    for (const item of chaves) localStorage.removeItem(item);
  } catch {
    // Ver acima: não há o que fazer, e não há o que quebrar.
  }
}

/** "há 2 minutos", "ontem" — o que a pessoa precisa saber sobre o que está vendo. */
export function quandoFoiGuardado(em: Date, agora = new Date()): string {
  const minutos = Math.floor((agora.getTime() - em.getTime()) / 60_000);
  if (minutos < 1) return 'agora mesmo';
  if (minutos < 60) return `há ${minutos} minuto${minutos === 1 ? '' : 's'}`;

  const horas = Math.floor(minutos / 60);
  if (horas < 24) return `há ${horas} hora${horas === 1 ? '' : 's'}`;

  const dias = Math.floor(horas / 24);
  return dias === 1 ? 'ontem' : `há ${dias} dias`;
}
